import { useMemo, useState } from 'react'
import classes from './ChatWidget.module.scss'
import { ChatBankAccount } from './types'
import { CurrencyFilter, currencyLabel, formatIban, resolveBankView } from './bankAccounts'

interface Props {
  accounts: ChatBankAccount[]
}

/**
 * Banka hesapları (IBAN) kartı: şirket tab'ı + döviz filtresi + satır bazında kopyalama.
 * "Tümü" seçiliyken banka başlıklı gruplar, döviz seçiliyken her banka tek satır çizilir.
 * Gelen veri olduğu gibi yansıtılır — IBAN doğrulanmaz, satır gizlenmez.
 * Türetilmiş durumun tamamı `resolveBankView`'da hesaplanır (bkz. bankAccounts.ts) — bu bileşen
 * yalnız sunum ve üç `useState`'ten ibarettir.
 */
export default function BankAccountsCard({ accounts }: Props) {
  const [companyIndex, setCompanyIndex] = useState(0)
  const [filter, setFilter] = useState<CurrencyFilter>('TRY')
  const [copied, setCopied] = useState<{ iban: string; failed: boolean } | null>(null)

  const view = useMemo(() => resolveBankView(accounts, companyIndex, filter), [accounts, companyIndex, filter])

  if (accounts.length === 0 || !view.activeCompany) return null

  const { companies, activeCompanyTitle, currencies, activeFilter, grouped, visibleAccounts, bankGroups, countLabel } =
    view
  // Tek dövizli şirkette filtre barı bilgi taşımaz (döviz zaten sayaçta belirtilir) — gizlenir.
  const singleCurrency = currencies.length <= 1

  async function copyIban(iban: string) {
    try {
      await navigator.clipboard.writeText(iban)
      setCopied({ iban, failed: false })
    } catch {
      setCopied({ iban, failed: true })
    }
    window.setTimeout(() => setCopied(current => (current?.iban === iban ? null : current)), 2000)
  }

  function renderRow(account: ChatBankAccount, label: 'currency' | 'bank') {
    const isCopied = copied?.iban === account.iban

    return (
      <div key={`${account.bankName}-${account.currency}-${account.iban}`} className={classes.bankRow}>
        {label === 'currency' ? (
          <span className={classes.bankCur}>{currencyLabel(account.currency)}</span>
        ) : (
          <span className={classes.bankName}>{account.bankName}</span>
        )}
        <span className={classes.bankIban}>{formatIban(account.iban)}</span>
        <button type='button' className={classes.bankCopyBtn} onClick={() => copyIban(account.iban)}>
          {isCopied ? (copied?.failed ? 'Kopyalanamadı' : 'Kopyalandı') : 'Kopyala'}
        </button>
      </div>
    )
  }

  return (
    <div className={classes.bankCard}>
      {companies.length > 1 && (
        <div className={classes.bankTabs}>
          {companies.map((company, index) => (
            <button
              key={company.key}
              type='button'
              aria-pressed={index === companyIndex}
              className={`${classes.bankTab} ${index === companyIndex ? classes.bankTabActive : ''}`}
              onClick={() => setCompanyIndex(index)}
            >
              {company.key}
            </button>
          ))}
        </div>
      )}

      {!singleCurrency && (
        <div className={classes.bankCurBar}>
          {['ALL' as CurrencyFilter, ...currencies].map(option => (
            <button
              key={option}
              type='button'
              aria-pressed={option === activeFilter}
              className={`${classes.bankCurPill} ${option === activeFilter ? classes.bankCurPillActive : ''}`}
              onClick={() => setFilter(option)}
            >
              {option === 'ALL' ? 'Tümü' : currencyLabel(option)}
            </button>
          ))}
        </div>
      )}

      {/* Faz-42: şirketin tam ticari unvanı; sekmede kısa ad kalır. */}
      {activeCompanyTitle && (
        <div className={classes.bankCompanyTitle} data-testid='bank-company-title'>
          {activeCompanyTitle}
        </div>
      )}

      <div className={classes.bankCount}>{countLabel}</div>

      {grouped
        ? bankGroups.map(bank => (
            <div key={bank.key}>
              <div className={classes.bankGroupHead}>{bank.key}</div>
              {bank.accounts.map(account => renderRow(account, 'currency'))}
            </div>
          ))
        : visibleAccounts.map(account => renderRow(account, 'bank'))}
    </div>
  )
}
